import jwtDecode from 'jwt-decode';
import { get } from 'lodash';
import { ITokens, ITokenStore, TokenTypes } from '../store';
import { BaseService } from './BaseService';
import { ConfigServiceActions } from './ConfigService';
import { CoreService } from './CoreService';
import { SDKAction, Unsubscribe } from './SDKEventService';

export const ERROR_CODE_TOKEN_NOT_FOUND = 'TOKEN_NOT_FOUND';

export interface BaseDecodedToken {
  readonly exp: number;
  readonly authSource?: string;
  readonly authScope?: string;
  readonly assumedBy?: string;
}

/*
export interface UserToken extends BaseDecodedToken {
  readonly email: string;
  readonly given_name: string;
  readonly family_name: string;
}
*/

export enum TokenServiceActions {
  UPDATE_TOKENS = 'TokenService/UPDATE_TOKENS',
}

export type UpdateTokensAction = SDKAction<{ id?: string; access?: string; refresh?: string; restricted?: string }>;

export class TokensService extends BaseService {
  private _tokenStore: ITokenStore | undefined;
  private _environmentSubscription?: Unsubscribe;

  constructor() {
    super({});

    this.trackEnvironment();
  }

  trackEnvironment() {
    if (this._environmentSubscription) {
      return;
    }

    let core: CoreService;
    try {
      core = this.core;
    } catch (error) {
      console.debug('Core service not set, skipping environment change subscription');
      return;
    }

    this._environmentSubscription = core.api.systemEvents.subscribe(ConfigServiceActions.UPDATE_CONFIG, () => {
      const environment = core.api.config.currentEnvironment;

      if (this._tokenStore && this._tokenStore.environment !== environment) {
        this._tokenStore.environment = environment;
        this.notifyTokens();
      }
    });
  }

  // workaround for getter behavior not being inherited if only the setter is overridden
  get core(): CoreService {
    return super.core;
  }

  set core(core: CoreService) {
    super.core = core;
    if (this._environmentSubscription) {
      this._environmentSubscription();
    }
    this.trackEnvironment();
  }

  decodedIdToken() {
    return this.id ? this.decode(this.id) as BaseDecodedToken : undefined;
  }


  async userInfo(): Promise<BaseDecodedToken> {
    await this.freshTokens();
    const token = this.decodedIdToken();
    // TODO fix exceptions convention
    if (!token) throw 'undefined token';
    return token;
  }

  async userId() {
    return get(await this.userInfo(), 'sub');
  }

  decode(jwt: string) {
    return decodeToken(jwt);
  }

  // TODO stop using any here
  async refreshTokens(): Promise<any> {
    if (this.refresh) {
      if (this.id && !this.decodedIdToken()?.authSource) {
        const token = await this.core.api.auth.refresh(this.refresh);
        this.updateTokens(token);
      } else {
        void this.clearImpersonation();
        throw {
          code: ERROR_CODE_TOKEN_NOT_FOUND,
          message: 'Refresh token not found',
        };
      }
    } else {
      void this.clear();
      throw {
        code: ERROR_CODE_TOKEN_NOT_FOUND,
        message: 'Refresh token not found',
      };
    }
  }

  // TODO evaluate / test / and potentially rewrite this method
  // THIS METHOD NEEDS AN OVERHAUL
  updateTokens(tokens: ITokens) {
    let mapToken: ITokens = {
      id: tokens.id,
      access: tokens.access,
      restricted: tokens.restricted,
    };

    if (tokens.refresh) {
      mapToken = {
        ...mapToken,
        refresh: tokens.refresh,
      };
    }

    this._tokenStore?.updateTokens(mapToken);
    this.notifyTokens();
  }

  notifyTokens() {
    const { id, refresh, access, restricted } = this;
    this.core.api.systemEvents.dispatch({
      action: TokenServiceActions.UPDATE_TOKENS,
      payload: { id, access, refresh, restricted },
    });
  }

  /**
   *
   * Make sure id token is valid and if it isn't then refresh it
   */
  async freshTokens() {
    if (this.expired()) {
      await this.refreshTokens();
    }

    return this;
  }

  // TODO stop using any here
  async clear(tokensToClear?: TokenTypes[]): Promise<any> {
    await this._tokenStore?.clear(tokensToClear);
    this.notifyTokens();
  }

  async clearImpersonation(): Promise<any> {
    await this.clear([TokenTypes.ID, TokenTypes.ACCESS]);
  }

  expired() {
    const id = this.decodedIdToken();
    return !id || tokenExpired(id);
  }

  set tokenStore(tokenStore: ITokenStore) {
    this._tokenStore = tokenStore;
  }

  get id() {
    return this._tokenStore?.id;
  }

  get refresh() {
    return this._tokenStore?.refresh;
  }

  get access() {
    return this._tokenStore?.access;
  }

  get restricted() {
    return this._tokenStore?.restricted;
  }

}


export function decodeToken<T extends BaseDecodedToken>(token: string) {
  return jwtDecode<T>(token);
}

/**
 * Checks to see if a token is expired
 */
export function tokenExpired({ exp }: BaseDecodedToken) {
  const currentTimestamp = Math.floor(Date.now() / 1000);
  return currentTimestamp >= exp;
}
